import { PersonRemove as PersonRemoveIcon, Restore as RestoreIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../utils/apiRequest";
import { PAGE_SIZES, usePersistedPagination } from "../utils/usePersistedPagination";
import { showToast } from "../utils/toastOptions";
import CsvWorkflow from "./CsvWorkflow";
import FilterMultiSelectWithSearch, { exactMulti } from "./FilterMultiSelectWithSearch";

const BASE = process.env.REACT_APP_API_BASE_URL;

const ADD_USERS_TEMPLATE_HEADERS = [
  "First Name",
  "Last Name*",
  "Email Address*",
  "Permission Template",
  "Company*",
  "Is Employee",
  "Employee Id",
  "Job Title",
  "Address",
  "City",
  "State",
  "Zip",
  "Country",
  "Business Phone",
  "Business Phone Extension",
  "Fax Number",
  "Mobile Phone",
  "Notes",
  "Work Classification",
  "Company Permission Template",
];

const ADD_USERS_EDITABLE_COLUMNS = [
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "email_address", label: "Email" },
  { key: "permission_template_name", label: "Permission Template" },
  { key: "is_employee", label: "Is Employee" },
  { key: "employee_id", label: "Employee ID" },
  { key: "job_title", label: "Job Title" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
  { key: "state_code", label: "State" },
  { key: "zip", label: "Zip" },
  { key: "country_code", label: "Country" },
  { key: "business_phone", label: "Business Phone" },
  { key: "business_phone_extension", label: "Phone Ext" },
  { key: "fax_number", label: "Fax" },
  { key: "mobile_phone", label: "Mobile Phone" },
  { key: "notes", label: "Notes" },
  { key: "work_classification_name", label: "Work Classification" },
  { key: "company_permission_template_name", label: "Company Perm Template" },
];

const downloadAddUsersTemplate = (companyName) => {
  const row = ADD_USERS_TEMPLATE_HEADERS.map((h) => (h === "Company*" ? companyName : ""));
  const csv = ADD_USERS_TEMPLATE_HEADERS.join(",") + "\n" + row.join(",") + "\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "daitaflow-user-import-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const buildAddUsersCommitMessage = (data) => {
  const created = data?.created?.length || 0;
  const existing = data?.existing?.length || 0;
  const failed = data?.failed?.length || 0;
  return `Created ${created}, existing ${existing}, failed ${failed}`;
};

const USER_STATUS_OPTIONS = new Set(["Active", "Inactive"]);
const USER_WHITELIST_OPTIONS = new Set(["Whitelisted", "Not Whitelisted"]);

function userStatusTags(user) {
  return {
    status: user?.is_active ? "Active" : "Inactive",
    whitelist: user?.is_whitelisted ? "Whitelisted" : "Not Whitelisted",
  };
}

function filterUsersByStatusAndWhitelist(row, _columnId, selected) {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  const { status, whitelist } = userStatusTags(row.original);
  const statusSelected = selected.filter((s) => USER_STATUS_OPTIONS.has(s));
  const whitelistSelected = selected.filter((s) => USER_WHITELIST_OPTIONS.has(s));
  const statusOk = statusSelected.length === 0 || statusSelected.includes(status);
  const whitelistOk = whitelistSelected.length === 0 || whitelistSelected.includes(whitelist);
  return statusOk && whitelistOk;
}

const formatLastLogin = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export default function AdminTab({
  users = [],
  thresholdDays,
  inputDays,
  onInputChange,
  onSubmit,
  variant = "company",
  showConfirmDialog = false,
  onConfirm,
  onCancel,
  showResetButton = false,
  thresholdDaysCompanyDefault = null,
  onReset,
  handleRestoreUser = () => {},
  checkboxSelection = false,
  onSelectionChange,
  companyId,
  externalSelection,
  // Project variant only: when set, render a per-row Remove button that
  // resolves the returned promise before clearing the row's loading state.
  onRemoveFromProject,
  paginationKey,
  companyName = "",
  onUsersImported,
  distributionGroups = [],
  distributionGroupsLoading = false,
  onDistributionGroupsOpen,
  onLoadDistributionGroup,
  onClearDistributionGroup,
}) {
  const [pagination, setPagination] = usePersistedPagination(paginationKey);
  const isCompany = variant === "company";
  const [tableUsers, setTableUsers] = useState(users);
  const [removingUserIds, setRemovingUserIds] = useState(() => new Set());

  const handleRemoveClick = useCallback(
    async (user) => {
      if (!onRemoveFromProject || removingUserIds.has(user.id)) return;
      setRemovingUserIds((prev) => {
        const next = new Set(prev);
        next.add(user.id);
        return next;
      });
      try {
        await onRemoveFromProject(user);
      } finally {
        setRemovingUserIds((prev) => {
          const next = new Set(prev);
          next.delete(user.id);
          return next;
        });
      }
    },
    [onRemoveFromProject, removingUserIds]
  );

  useEffect(() => {
    setTableUsers(users);
  }, [users]);

  const fetchWhitelistedUsers = useCallback(async () => {
    try {
      const data = await apiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/companies/${companyId}/users/whitelist/`,
        { method: "GET" }
      );
      const whitelistedIds = new Set((data.whitelisted_users || []).map((u) => u.user_id));

      setTableUsers((prev) =>
        prev.map((u) => ({
          ...u,
          is_whitelisted: whitelistedIds.has(u.id),
        }))
      );
    } catch (err) {
      showToast("error", err.message || "Failed to fetch whitelisted users");
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) fetchWhitelistedUsers();
  }, [companyId, fetchWhitelistedUsers]);

  const columns = useMemo(() => {
    const base = [
      {
        id: "name",
        accessorKey: "name",
        header: "User",
        minSize: 220,
        size: 240,
        muiTableHeadCellProps: {
          sx: {
            verticalAlign: "top",
            "& .MuiCollapse-wrapperInner": {
              display: "flex",
              alignItems: "flex-end",
              minHeight: 48,
              width: "100%",
            },
          },
        },
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterVariant: "multi-select",
        filterFn: filterUsersByStatusAndWhitelist,
        Filter: FilterMultiSelectWithSearch,
        meta: {
          getFilterTags: (row) => {
            const { status, whitelist } = userStatusTags(row.original);
            return [status, whitelist];
          },
        },
        Cell: ({ row }) => {
          const user = row.original;
          const isActive = Boolean(user.is_active);
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <Box
                  sx={{
                    fontWeight: 500,
                    color: isActive ? "success.main" : "error.main",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.name || "—"}
                </Box>
                {!isActive && (
                  <Tooltip title="Reactivate user">
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Reactivate user"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestoreUser(user.id);
                      }}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ color: "text.secondary", fontSize: "0.85em" }}>
                {user.is_whitelisted ? "Whitelisted" : "Not Whitelisted"}
              </Box>
            </Box>
          );
        },
      },
      {
        id: "days_until_termination",
        header: "Days Until Termination",
        minSize: 200,
        size: 200,
        accessorFn: (row) => {
          const t = Number(thresholdDays);
          if (!Number.isFinite(t)) return null;
          
          let lastActivityDate = row?.last_login_at ? new Date(row.last_login_at) : NaN;
          const lastActivatedDate = row?.last_activated_at ? new Date(row.last_activated_at) : new Date(row?.created_at);
          if (isNaN(lastActivityDate) || (lastActivatedDate && lastActivatedDate > lastActivityDate)) {
            lastActivityDate = lastActivatedDate;
          }

          const ts = lastActivityDate instanceof Date ? lastActivityDate.getTime() : NaN;
          if (!Number.isFinite(ts)) return null;
          const daysSince = Math.floor((Date.now() - ts) / 86_400_000);
          return t - daysSince;
        },
        filterVariant: "range",
        filterFn: "betweenInclusive",
        muiFilterTextFieldProps: ({ rangeFilterIndex }) => ({ placeholder: rangeFilterIndex === 0 ? "Min" : "Max" }),
        Cell: ({ cell }) => {
          const v = cell.getValue();
          if (v == null) return "N/A";
          return v;
        },
        enableSorting: false,
        enableGlobalFilter: false,
      },
      {
        accessorKey: "email_address",
        header: "Email",
        minSize: 300,
        size: 300,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
        enableGlobalFilter: false,
      },
      {
        id: "company_permission_template",
        header: "Company Permission Template",
        minSize: 230,
        size: 230,
        accessorFn: (row) => row?.company_permission_template?.name ?? null,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
        enableGlobalFilter: false,
      },
      {
        id: "permission_template",
        header: "Project Permission Template",
        minSize: 220,
        size: 220,
        accessorFn: (row) => row?.permission_template?.name ?? null,
        filterVariant: "multi-select",
        filterFn: exactMulti,
        Filter: FilterMultiSelectWithSearch,
        Cell: ({ cell }) => cell.getValue() ?? "—",
        enableGlobalFilter: false,
      },
      {
        id: "last_login_at",
        header: "Last Login",
        size: 180,
        accessorFn: (row) => row.last_login_at,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "never", label: "Never logged in" },
          { value: "30", label: "> 30 days ago" },
          { value: "60", label: "> 60 days ago" },
          { value: "90", label: "> 90 days ago" },
          { value: "120", label: "> 120 days ago" },
          { value: "180", label: "> 180 days ago" },
        ],
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const v = row.getValue(columnId);
          if (filterValue === "never") return !v;
          const ts = Date.parse(v ?? "");
          if (!Number.isFinite(ts)) return true;
          const days = Math.floor((Date.now() - ts) / 86_400_000);
          return days > Number(filterValue);
        },
        Cell: ({ row: tableRow }) => {
          if (tableRow.original.last_login_at) {
            const lastLogin = formatLastLogin(tableRow.original.last_login_at);
            return (
              <Typography variant="body2" color="text.secondary">
                {lastLogin}
              </Typography>
            );
          } else if (tableRow.original.last_activated_at) {
            const activated = formatLastLogin(tableRow.original.last_activated_at);
              return (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  {activated !== "—" ? `Never (activated at ${activated})` : "Never"}
                </Typography>
              );
          } else if (tableRow.original.created_at) {
            const created = formatLastLogin(tableRow.original.created_at);
            return (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {created !== "—" ? `Never (created at ${created})` : "Never"}
              </Typography>
            );
          }
          return "—";
        },
        enableGlobalFilter: false,
      },
      {
        id: "last_activated_at",
        header: "Last Activated",
        size: 160,
        accessorFn: (row) => row.last_activated_at,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "never", label: "Never logged in" },
          { value: "30", label: "> 30 days ago" },
          { value: "60", label: "> 60 days ago" },
          { value: "90", label: "> 90 days ago" },
          { value: "120", label: "> 120 days ago" },
          { value: "180", label: "> 180 days ago" },
        ],
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const v = row.getValue(columnId);
          if (filterValue === "never") return !v;
          const ts = Date.parse(v ?? "");
          if (!Number.isFinite(ts)) return true;
          const days = Math.floor((Date.now() - ts) / 86_400_000);
          return days > Number(filterValue);
        },
        Cell: ({ row: tableRow }) => {
          if (tableRow.original.last_activated_at) {
            const activated = formatLastLogin(tableRow.original.last_activated_at);
            return (
              <Typography variant="body2" color="text.secondary">
                {activated}
              </Typography>
            );
          } else if (tableRow.original.created_at) {
            const created = formatLastLogin(tableRow.original.created_at);
            return (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {created !== "—" ? `Never (created at ${created})` : "Never"}
              </Typography>
            );
          }
          return "—";
        },
      },
      {
        id: "created_at",
        header: "Created At",
        size: 160,
        accessorFn: (row) => row.created_at,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "never", label: "Never logged in" },
          { value: "30", label: "> 30 days ago" },
          { value: "60", label: "> 60 days ago" },
          { value: "90", label: "> 90 days ago" },
          { value: "120", label: "> 120 days ago" },
          { value: "180", label: "> 180 days ago" },
        ],
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const v = row.getValue(columnId);
          if (filterValue === "never") return !v;
          const ts = Date.parse(v ?? "");
          if (!Number.isFinite(ts)) return true;
          const days = Math.floor((Date.now() - ts) / 86_400_000);
          return days > Number(filterValue);
        },
        Cell: ({ cell }) => formatLastLogin(cell.getValue()),
      },
    ];
    if (!isCompany && onRemoveFromProject) {
      base.push({
        id: "remove_from_project",
        header: "Quick Actions",
        minSize: 130,
        size: 130,
        muiTableHeadCellProps: { align: "center", sx: { verticalAlign: "top" } },
        muiTableBodyCellProps: { align: "center" },
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        Cell: ({ row }) => {
          const u = row.original;
          const isRemoving = removingUserIds.has(u.id);
          return (
            <Tooltip title="Remove from project">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={isRemoving}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveClick(u);
                  }}
                >
                  {isRemoving ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PersonRemoveIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      });
    }
    return base;
  }, [
    isCompany,
    thresholdDays,
    handleRestoreUser,
    onRemoveFromProject,
    removingUserIds,
    handleRemoveClick,
  ]);

  // MRT selection -> parent Set
  const [rowSelection, setRowSelection] = useState({});

  useEffect(() => {
    if (!externalSelection) return;
    const sel = {};
    for (const id of externalSelection) sel[String(id)] = true;
    setRowSelection(sel);
  }, [externalSelection]);

  useEffect(() => {
    if (!onSelectionChange) return;
    const selected = new Set(
      Object.entries(rowSelection)
        .filter(([, v]) => Boolean(v))
        .map(([k]) => parseInt(k, 10))
    );
    onSelectionChange(selected);
  }, [rowSelection, onSelectionChange]);

  const table = useMaterialReactTable({
    /* ── Data & identity ─────────────────────────────────────────────── */
    columns,
    data: tableUsers,
    getRowId: (row) => String(row.id),

    /* ── Feature toggles ─────────────────────────────────────────────── */
    enableColumnActions: false,
    enableSorting: false,
    enablePagination: true,
    enableRowSelection: checkboxSelection,
    selectAllMode: "all", // select across whole dataset
    enableColumnResizing: true,
    layoutMode: "grid",

    /* ── Filtering & search ──────────────────────────────────────────── */
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableFilterMatchHighlighting: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    positionGlobalFilter: "left",
    muiSearchTextFieldProps: {
      placeholder: "Search users by name",
    },
    muiTableHeadCellProps: {
      sx: {
        "& .MuiCollapse-wrapperInner": {
          display: "flex",
          alignItems: "flex-end",
          minHeight: 48,
        },
      },
    },

    /* ── Controlled state ────────────────────────────────────────────── */
    state: { rowSelection, pagination },
    onRowSelectionChange: setRowSelection,

    /* ── Pagination ──────────────────────────────────────────────────── */
    onPaginationChange: setPagination,
    paginationDisplayMode: "pages",
    muiPaginationProps: { rowsPerPageOptions: PAGE_SIZES },
    muiBottomToolbarProps: {
      sx: {
        minHeight: "auto",
        "& > .MuiBox-root > .MuiBox-root": { position: "relative", top: "auto", right: "auto" },
        "& .MuiTablePagination-root": { py: 0 },
      },
    },

    /* ── Initial table state ─────────────────────────────────────────── */
    initialState: {
      density: "compact",
      pagination: { pageSize: 10, pageIndex: 0 },
      showGlobalFilter: true,
      showColumnFilters: true, // open filters panel by default
      columnPinning: { left: ["mrt-row-select", "name"] },
      columnVisibility: {
        last_activated_at: false,
        created_at: false,
      },
    },

    /* ── Toolbar (right-side cluster) ────────────────────────────────── */
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Filters */}
        <Button
          variant="outlined"
          onClick={() => {
            table.resetColumnFilters();
            table.resetGlobalFilter();
          }}
        >
          Clear Filters
        </Button>
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* View controls */}
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ShowHideColumnsButton table={table} />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleDensePaddingButton table={table} />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),

    /* ── Row UX ──────────────────────────────────────────────────────── */
    muiTableBodyRowProps: ({ row }) => ({
      // only toggle selection when clicking row background
      onClick: (e) => {
        if (e.target.closest("button, [role='button'], a, input, label")) return;
        row.getToggleSelectedHandler()(e);
      },
      sx: { cursor: "pointer" },
    }),

    /* ── Misc ───────────────────────────────────────────────────────── */
    positionToolbarAlertBanner: "none",
  });

  const renderThresholdControls = () => (
    <Box
      className="threshold-container"
      sx={{
        m: "0 !important",
        ...(isCompany ? { mt: "16px !important" } : {}),
        flexWrap: "nowrap",
        width: isCompany ? "100%" : undefined,
        "& label": { whiteSpace: "nowrap", flexShrink: 0, lineHeight: 1.2 },
      }}
    >
      <label>{isCompany ? "Default Inactive Days Threshold:" : "Inactive Days Threshold:"}</label>
      <input
        id="threshold-input"
        type="number"
        value={inputDays}
        onChange={onInputChange}
        className="threshold-input"
        style={
          isCompany
            ? { width: "4rem", height: 32, padding: "0.15rem 0.4rem", boxSizing: "border-box" }
            : undefined
        }
      />
      <Button variant="contained" onClick={onSubmit} sx={{ flexShrink: 0 }}>
        Submit
      </Button>
      {showResetButton && (
        <Button variant="outlined" color="warning" onClick={onReset} sx={{ flexShrink: 0 }}>
          Reset to company default: {thresholdDaysCompanyDefault}
        </Button>
      )}
    </Box>
  );

  const renderDistributionGroup = () => (
    <Stack direction="row" spacing={1} alignItems="center">
      <Autocomplete
        size="small"
        sx={{ flexShrink: 0, width: "100%" }}
        options={distributionGroups}
        getOptionLabel={(g) => `${g.name}${g.project_name ? ` (${g.project_name})` : ""}`}
        onChange={(_, g) => onLoadDistributionGroup?.(g)}
        onOpen={distributionGroups.length === 0 ? onDistributionGroupsOpen : undefined}
        loading={distributionGroupsLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Load Distribution Group"
            placeholder="Select a group to check its members"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {distributionGroupsLoading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderOption={(props, g) => (
          <li {...props} key={`${g.project_id}-${g.id}`}>
            <Stack>
              <Typography variant="body2">{g.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {(g.users || []).length} member{(g.users || []).length === 1 ? "" : "s"}
                {g.project_name ? ` · ${g.project_name}` : ""}
              </Typography>
            </Stack>
          </li>
        )}
      />
      {Boolean(externalSelection) && (
        <Button size="small" onClick={onClearDistributionGroup} sx={{ flexShrink: 0 }}>
          Clear
        </Button>
      )}
    </Stack>
  );

  const usersTable = (
    <Box sx={{ mt: 1 }}>
      <MaterialReactTable table={table} />
    </Box>
  );

  return (
    <>
      {isCompany ? (
        <CsvWorkflow
          compact
          title="Add Users"
          reviewTitle="Table of Change"
          helperText="Required columns are marked with *."
          previewUrl={`${BASE}/csv/users/preview/`}
          commitUrl={`${BASE}/csv/users/commit/`}
          editableColumns={ADD_USERS_EDITABLE_COLUMNS}
          onDownloadTemplate={() => downloadAddUsersTemplate(companyName)}
          onCommitSuccess={onUsersImported}
          buildCommitMessage={buildAddUsersCommitMessage}
          renderLayout={({ dropzone, review }) => (
            <>
              <Stack direction="row" spacing={2} alignItems="stretch" sx={{ mt: 1.5, mb: 1 }}>
                <Stack
                  spacing={1.25}
                  sx={{ width: "fit-content", flexShrink: 0, justifyContent: "center" }}
                >
                  {renderDistributionGroup()}
                  {renderThresholdControls()}
                </Stack>
                <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                  <Box sx={{ flex: 1 }}>{dropzone}</Box>
                </Box>
              </Stack>
              {review}
              {usersTable}
            </>
          )}
        />
      ) : (
        <>
          {renderThresholdControls()}
          {usersTable}
        </>
      )}

      {showConfirmDialog && (
        <Dialog open={showConfirmDialog} onClose={onCancel}>
          <DialogTitle>Confirm Threshold Change</DialogTitle>
          <DialogContent dividers>
            <p>
              WARNING: You are setting {isCompany ? "Company" : "Project"} Default Threshold Days to{" "}
              <strong>{inputDays}</strong> days. Please confirm.
            </p>
            <p>This will kick users that have exceeded or reached the new threshold.</p>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={onCancel} color="inherit">
              Cancel
            </Button>
            <Button onClick={onConfirm} variant="outlined" color="error">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

AdminTab.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object),
  thresholdDays: PropTypes.number,
  inputDays: PropTypes.number,
  onInputChange: PropTypes.func,
  onSubmit: PropTypes.func,
  variant: PropTypes.oneOf(["company", "project"]),
  companyName: PropTypes.string,
  onUsersImported: PropTypes.func,
  distributionGroups: PropTypes.arrayOf(PropTypes.object),
  distributionGroupsLoading: PropTypes.bool,
  onDistributionGroupsOpen: PropTypes.func,
  onLoadDistributionGroup: PropTypes.func,
  onClearDistributionGroup: PropTypes.func,
};
